import { useState } from 'react';
import { motion } from 'framer-motion';
import { MODULES } from './data';

// MODULES — module spec sheet ledger. Each module is a row with API endpoints + RPS.
// NOT a generic "feature card" grid.
export default function Modules({ setPage }) {
  const [active, setActive] = useState('@mc/edge');
  const m = MODULES.find(x => x.code === active) || MODULES[0];
  return (
    <div className="bg-[#0A0E14] text-[#E6EDF3] font-mono min-h-screen">
      <section className="pt-32 pb-8 max-w-[1500px] mx-auto px-5 lg:px-8 border-b border-[#1A2230]">
        <p className="text-[10px] tracking-[0.32em] text-[#06D6A0] uppercase mb-3">// console / modules</p>
        <h1 className="font-sans text-5xl lg:text-7xl tracking-[-0.025em] lowercase leading-[0.98] font-medium">four modules.<br /><span className="text-[#5F7184]">one cli.</span></h1>
      </section>

      <section className="max-w-[1500px] mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-[280px_1fr] gap-10 items-start">
        {/* LEFT — sidebar like a finder pane */}
        <aside className="border border-[#1A2230] rounded-sm bg-[#0E141C]">
          <div className="px-4 py-2.5 border-b border-[#1A2230] text-[10px] text-[#5F7184]">// modules · 4</div>
          <ul>
            {MODULES.map(mod => (
              <li key={mod.code}>
                <button onClick={() => setActive(mod.code)} data-testid={`mc-mod-${mod.code}`} className={`w-full text-left px-4 py-3 border-b border-[#1A2230] last:border-0 transition-colors ${active === mod.code ? 'bg-[#06D6A0]/10 border-l-2 border-l-[#06D6A0]' : 'hover:bg-[#1A2230]/50'}`}>
                  <p className={`text-sm ${active === mod.code ? 'text-[#06D6A0]' : 'text-[#E6EDF3]'}`}>{mod.code}</p>
                  <p className="text-[10px] text-[#5F7184] mt-0.5">{mod.tag} · {mod.endpoints} endpoints</p>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* RIGHT — module spec sheet */}
        <motion.article key={m.code} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="border border-[#1A2230] bg-[#0E141C] rounded-sm">
          <div className="px-6 py-4 border-b border-[#1A2230] flex items-baseline justify-between">
            <div>
              <p className="text-[10px] tracking-[0.3em] text-[#5F7184] uppercase">{m.tag}</p>
              <p className="text-2xl text-[#E6EDF3]">{m.code}</p>
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#06D6A0] border border-[#06D6A0]/30 px-2 py-0.5">stable · 8.4.2</p>
          </div>
          <div className="p-6 lg:p-9 grid lg:grid-cols-[1.4fr_1fr] gap-10">
            <div>
              <h2 className="font-sans text-3xl text-[#E6EDF3] tracking-[-0.02em] leading-[1.05] lowercase font-medium">{m.name}</h2>
              <p className="font-sans text-sm text-[#5F7184] mt-3 leading-[1.7]">{m.d}</p>

              <p className="text-[10px] tracking-[0.3em] uppercase text-[#5F7184] mt-9 mb-3">// install</p>
              <pre className="bg-[#0A0E14] border border-[#1A2230] p-3.5 text-[#06D6A0] text-xs leading-relaxed">$ npm i {m.code}<br /><span className="text-[#5F7184]">added 1 package · audited 442 packages · 0 vulnerabilities</span></pre>

              <p className="text-[10px] tracking-[0.3em] uppercase text-[#5F7184] mt-7 mb-3">// quickstart</p>
              <pre className="bg-[#0A0E14] border border-[#1A2230] p-3.5 text-xs leading-relaxed text-[#E6EDF3] overflow-x-auto">
<span className="text-[#5F7184]">// {m.name.toLowerCase()}.example.ts</span>
{`\n`}<span className="text-[#FFB454]">import</span> {`{ ${m.code.split('/')[1]} }`} <span className="text-[#FFB454]">from</span> <span className="text-[#06D6A0]">'{m.code}'</span>
{`\n\n`}<span className="text-[#FFB454]">const</span> client = <span className="text-[#FFB454]">new</span> {m.code.split('/')[1]}({`{`}
{`\n  region: `}<span className="text-[#06D6A0]">'us-east-1'</span>,
{`\n  token: process.env.MC_TOKEN`}
{`\n})`}
{`\n\n`}<span className="text-[#FFB454]">await</span> client.run()
              </pre>
            </div>
            <div className="space-y-5">
              <Stat k="API endpoints" v={m.endpoints} />
              <Stat k="Throughput" v={m.rps} />
              <Stat k="Regions live" v="18 / 18" />
              <Stat k="SLA" v="99.99%" />
              <Stat k="Compliance" v="SOC2 · HIPAA · FedRAMP" />
              <button onClick={() => setPage('pricing')} className="w-full bg-[#06D6A0] text-[#0A0E14] py-3 text-sm hover:bg-[#5BFFC9] transition-colors mt-2" data-testid="mc-mod-cta">$ start using {m.code} →</button>
            </div>
          </div>
        </motion.article>
      </section>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="border border-[#1A2230] p-3.5 bg-[#0A0E14]">
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#5F7184]">{k}</p>
      <p className="text-base text-[#E6EDF3] mt-1">{v}</p>
    </div>
  );
}
